import React, { useRef, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';
import GroupedColumnPickerPanel, {
  useCloseOnOutsideClick,
  type ColumnPickerGroup,
} from '../../CrewingAnalytics/components/GroupedColumnPickerPanel';
import { WES_DIMENSIONS, type WesDimKey } from '../wesPalletStagingTable';
import WesPressable from './WesPressable';

export const WES_LAST_DIMENSION_MESSAGE = 'At least one dimension column must remain visible.';

// Extra selected key handed to the shared panel so it never silently ignores an attempt to untick the last dimension; it is stripped before use.
const GUARD_KEY = '__wes_guard__';

const GROUPS: ColumnPickerGroup[] = [
  { key: 'geography', label: 'Geography', options: WES_DIMENSIONS.filter((d) => d.group === 'geography').map((d) => ({ key: d.key, label: d.label })) },
  { key: 'timeframe', label: 'Timeframe', options: WES_DIMENSIONS.filter((d) => d.group === 'timeframe').map((d) => ({ key: d.key, label: d.label })) },
];

interface WesColumnPickerProps {
  dimensions: WesDimKey[];
  onChange: (keys: WesDimKey[]) => void;
}

const WesColumnPicker: React.FC<WesColumnPickerProps> = ({ dimensions, onChange }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const wrapRef = useRef<View>(null);
  useCloseOnOutsideClick(wrapRef, () => setOpen(false), open);

  const handleChange = (keys: string[]) => {
    const next = keys.filter((key) => key !== GUARD_KEY) as WesDimKey[];
    if (next.length === 0) {
      setMessage(WES_LAST_DIMENSION_MESSAGE);
      return;
    }
    // Deselect All leaves a single dimension (no guard key in the result): allowed, with the message.
    setMessage(!keys.includes(GUARD_KEY) && next.length === 1 ? WES_LAST_DIMENSION_MESSAGE : '');
    onChange(next);
  };

  return (
    <View ref={wrapRef} style={styles.wrap}>
      <WesPressable style={styles.pill} hoverStyle={styles.pillHover} onPress={() => setOpen((prev) => !prev)} testID="wes-columns-btn">
        <Text style={styles.pillText}>{`Columns (${dimensions.length})`}</Text>
      </WesPressable>
      {open ? (
        <View testID="wes-columns-panel">
          <GroupedColumnPickerPanel
            groups={GROUPS}
            selectedKeys={[...dimensions, GUARD_KEY]}
            onChange={handleChange}
            hoverEffects
            defaultExpanded
            notice={message ? <Text style={styles.message} testID="wes-columns-message" accessibilityRole="alert">{message}</Text> : null}
          />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { position: 'relative', zIndex: 40 },
  pill: {
    height: 32,
    justifyContent: 'center',
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#005CBC',
    backgroundColor: '#FFFFFF',
  },
  pillHover: { backgroundColor: '#EFF5FC' },
  pillText: { fontSize: 13, color: '#005CBC', fontWeight: '600', fontFamily: FontFamily.medium },
  message: { marginTop: 8, padding: 8, borderRadius: 4, backgroundColor: '#FFF3CD', fontSize: 11, fontFamily: FontFamily.regular, color: '#664D03' },
});

export default WesColumnPicker;
